import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Navigate } from "react-router";

import {
  CenteredLoader,
  EditableListField,
  EditableTextarea,
  PageIntro,
  SegmentedFilter,
} from "@/components/common";
import { getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { trpc } from "@/lib/trpc";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@runcheetah/ui";
import { toast } from "sonner";

type SectionKey = "basics" | "positioning" | "insight" | "proof" | "story";

type ProfileForm = {
  brandName: string;
  tagline: string;
  mission: string;
  vision: string;
  marketCategory: string;
  marketSubcategoryNiche: string;
  differentiationStrategy: string;
  behavioralTraits: string;
  brandPromise: string;
  brandStoryOrigin: string;
  brandArchetype: string;
  perceivedPositioning: string[];
  buyingTriggers: string[];
  decisionMakingDrivers: string[];
  desiresAspirations: string[];
  competitiveAdvantages: string[];
  keyDifferentiators: string[];
  painPoints: string[];
  competitiveDisadvantages: string[];
  coreValues: string[];
};

const FIELD_MAP: Record<keyof ProfileForm, string> = {
  brandName: "coreIdentity.brandName",
  tagline: "coreIdentity.taglineOrSlogan",
  mission: "coreIdentity.mission",
  vision: "coreIdentity.vision",
  marketCategory: "positioningMarketContext.marketCategory",
  marketSubcategoryNiche: "positioningMarketContext.marketSubcategoryNiche",
  differentiationStrategy: "positioningMarketContext.differentiationStrategy",
  behavioralTraits: "targetAudience.behavioralTraits",
  brandPromise: "coreIdentity.brandPromise",
  brandStoryOrigin: "coreIdentity.brandStoryOrigin",
  brandArchetype: "coreIdentity.brandArchetype",
  perceivedPositioning: "positioningMarketContext.perceivedPositioning",
  buyingTriggers: "targetAudience.buyingTriggers",
  decisionMakingDrivers: "targetAudience.decisionMakingDrivers",
  desiresAspirations: "targetAudience.desiresAspirations",
  competitiveAdvantages: "positioningMarketContext.competitiveAdvantages",
  keyDifferentiators: "coreIdentity.keyDifferentiators",
  painPoints: "targetAudience.painPoints",
  competitiveDisadvantages: "positioningMarketContext.competitiveDisadvantages",
  coreValues: "coreIdentity.coreValues",
};

const EMPTY_FORM: ProfileForm = {
  brandName: "",
  tagline: "",
  mission: "",
  vision: "",
  marketCategory: "",
  marketSubcategoryNiche: "",
  differentiationStrategy: "",
  behavioralTraits: "",
  brandPromise: "",
  brandStoryOrigin: "",
  brandArchetype: "",
  perceivedPositioning: [],
  buyingTriggers: [],
  decisionMakingDrivers: [],
  desiresAspirations: [],
  competitiveAdvantages: [],
  keyDifferentiators: [],
  painPoints: [],
  competitiveDisadvantages: [],
  coreValues: [],
};

function Section({
  title,
  badge,
  children,
}: {
  title: string;
  badge?: string;
  children: ReactNode;
}) {
  return (
    <Card className="border-border/70 bg-card/90 shadow-sm">
      <CardHeader className="space-y-3 pb-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <CardTitle className="text-lg">{title}</CardTitle>
          {badge ? <Badge variant="outline">{badge}</Badge> : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">{children}</CardContent>
    </Card>
  );
}

function OptionalEditableTextarea({
  label,
  description,
  value,
  placeholder,
  onChange,
  onSave,
  rows,
}: {
  label: string;
  description: string;
  value: string;
  placeholder: string;
  onChange: (value: string) => void;
  onSave: (value: string) => Promise<unknown> | unknown;
  rows?: number;
}) {
  const intl = useIntl();
  const [isVisible, setIsVisible] = useState(Boolean(value.trim()));

  useEffect(() => {
    if (value.trim()) setIsVisible(true);
  }, [value]);

  if (!isVisible) {
    return (
      <div className="space-y-3 rounded-2xl border border-dashed border-border/70 p-4">
        <div>
          <p className="text-sm font-medium">{label}</p>
          <p className="mt-1 text-xs leading-5 text-muted-foreground">{description}</p>
        </div>
        <Button type="button" variant="outline" onClick={() => setIsVisible(true)}>
          {intl.formatMessage(
            { id: "brandProfile.optionalField.add", defaultMessage: "Add {field}" },
            { field: label.toLocaleLowerCase() },
          )}
        </Button>
      </div>
    );
  }

  return (
    <EditableTextarea
      label={label}
      description={description}
      value={value}
      onChange={onChange}
      onSave={onSave}
      placeholder={placeholder}
      rows={rows}
    />
  );
}

type CompetitorCategory = "direct" | "indirect" | "substitute";
type CompetitorRow = {
  id: string;
  name: string;
  url: string;
  category: CompetitorCategory;
};

const toCompetitorRows = (
  direct: [string, string][] | null | undefined,
  indirect: [string, string][] | null | undefined,
  substitutes: [string, string][] | null | undefined,
): CompetitorRow[] =>
  (
    [
      ["direct", direct ?? []],
      ["indirect", indirect ?? []],
      ["substitute", substitutes ?? []],
    ] as const
  ).flatMap(([category, items]) =>
    items.map(([name, url], index) => ({
      id: `${category}-${index}-${name}`,
      name,
      url,
      category,
    })),
  );

function CompetitorEditor({
  direct,
  indirect,
  substitutes,
  onSave,
}: {
  direct: [string, string][] | null | undefined;
  indirect: [string, string][] | null | undefined;
  substitutes: [string, string][] | null | undefined;
  onSave: (buckets: {
    direct: [string, string][];
    indirect: [string, string][];
    substitutes: [string, string][];
  }) => Promise<unknown>;
}) {
  const intl = useIntl();
  const [rows, setRows] = useState<CompetitorRow[]>(() =>
    toCompetitorRows(direct, indirect, substitutes),
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(
    () => setRows(toCompetitorRows(direct, indirect, substitutes)),
    [direct, indirect, substitutes],
  );

  const update = (id: string, patch: Partial<CompetitorRow>) =>
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const saveRows = async () => {
    const buckets = {
      direct: [] as [string, string][],
      indirect: [] as [string, string][],
      substitutes: [] as [string, string][],
    };
    for (const row of rows) {
      const value: [string, string] = [row.name.trim(), row.url.trim()];
      if (!value[0] || !value[1]) continue;
      if (row.category === "direct") buckets.direct.push(value);
      else if (row.category === "indirect") buckets.indirect.push(value);
      else buckets.substitutes.push(value);
    }
    setIsSaving(true);
    try {
      await onSave(buckets);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-2xl border border-border/70 bg-background/55 p-4">
      <div>
        <p className="text-sm font-medium text-foreground">
          {intl.formatMessage({
            id: "brandProfile.competitors.label",
            defaultMessage: "Competitors and alternatives",
          })}
        </p>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          {intl.formatMessage({
            id: "brandProfile.competitors.description",
            defaultMessage: "Each competitor belongs to one category only.",
          })}
        </p>
      </div>
      {rows.map((row) => (
        <div key={row.id} className="grid gap-2 lg:grid-cols-[1fr_1.4fr_11rem_auto]">
          <Input
            aria-label={intl.formatMessage({
              id: "brandProfile.competitors.nameAria",
              defaultMessage: "Competitor name",
            })}
            value={row.name}
            placeholder={intl.formatMessage({
              id: "brandProfile.competitors.namePlaceholder",
              defaultMessage: "Name",
            })}
            onChange={(event) => update(row.id, { name: event.target.value })}
          />
          <Input
            aria-label={intl.formatMessage({
              id: "brandProfile.competitors.urlAria",
              defaultMessage: "Competitor URL",
            })}
            value={row.url}
            placeholder={intl.formatMessage({
              id: "brandProfile.competitors.urlPlaceholder",
              defaultMessage: "https://example.com",
            })}
            onChange={(event) => update(row.id, { url: event.target.value })}
          />
          <Select
            value={row.category}
            onValueChange={(category) =>
              update(row.id, { category: category as CompetitorCategory })
            }
          >
            <SelectTrigger
              aria-label={intl.formatMessage({
                id: "brandProfile.competitors.categoryAria",
                defaultMessage: "Competitor category",
              })}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="direct">
                {intl.formatMessage({
                  id: "brandProfile.competitors.direct",
                  defaultMessage: "Direct",
                })}
              </SelectItem>
              <SelectItem value="indirect">
                {intl.formatMessage({
                  id: "brandProfile.competitors.indirect",
                  defaultMessage: "Indirect",
                })}
              </SelectItem>
              <SelectItem value="substitute">
                {intl.formatMessage({
                  id: "brandProfile.competitors.substitute",
                  defaultMessage: "Substitute",
                })}
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}
          >
            {intl.formatMessage({
              id: "brandProfile.competitors.remove",
              defaultMessage: "Remove",
            })}
          </Button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={() =>
            setRows((current) => [
              ...current,
              {
                id: `new-${crypto.randomUUID()}`,
                name: "",
                url: "",
                category: "direct",
              },
            ])
          }
        >
          {intl.formatMessage({
            id: "brandProfile.competitors.add",
            defaultMessage: "Add competitor",
          })}
        </Button>
        <Button type="button" disabled={isSaving} onClick={saveRows}>
          {isSaving
            ? intl.formatMessage({ id: "common.saving", defaultMessage: "Saving..." })
            : intl.formatMessage({
                id: "brandProfile.competitors.save",
                defaultMessage: "Save competitors",
              })}
        </Button>
      </div>
    </div>
  );
}

function ReadOnlyAudienceItem({
  title,
  description,
  footer,
}: {
  title: string;
  description: string;
  footer?: ReactNode;
}) {
  return (
    <div className="space-y-3 rounded-2xl border border-border/70 bg-background/55 p-4">
      <div className="space-y-1.5">
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-sm leading-6 text-muted-foreground">{description}</p>
      </div>
      {footer ? <div className="text-xs text-muted-foreground">{footer}</div> : null}
    </div>
  );
}

export function DeepAudienceInsights({
  humanLenses,
  domainTerritories,
}: {
  humanLenses: Array<{ name: string; description: string; audienceTension: string }>;
  domainTerritories: Array<{ name: string; description: string }>;
}) {
  const intl = useIntl();

  if (humanLenses.length === 0 && domainTerritories.length === 0) return null;

  return (
    <div className="space-y-5 rounded-3xl border border-dashed border-border/80 bg-muted/25 p-5">
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">
          {intl.formatMessage({
            id: "brandProfile.deepAudience.title",
            defaultMessage: "Deep Audience Model",
          })}
        </p>
        <p className="text-sm leading-6 text-muted-foreground">
          {intl.formatMessage({
            id: "brandProfile.deepAudience.description",
            defaultMessage:
              "Read-only audience strategy signals generated from external public evidence.",
          })}
        </p>
      </div>

      {humanLenses.length ? (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {intl.formatMessage({
              id: "brandProfile.humanLenses.label",
              defaultMessage: "Human Lenses",
            })}
          </p>
          <div className="grid gap-3 lg:grid-cols-2">
            {humanLenses.map((lens, index) => (
              <ReadOnlyAudienceItem
                key={`${lens.name}-${index}`}
                title={lens.name}
                description={lens.description}
                footer={intl.formatMessage(
                  {
                    id: "brandProfile.humanLenses.tension",
                    defaultMessage: "Tension: {tension}",
                  },
                  { tension: lens.audienceTension },
                )}
              />
            ))}
          </div>
        </div>
      ) : null}

      {domainTerritories.length ? (
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {intl.formatMessage({
              id: "brandProfile.domainTerritories.label",
              defaultMessage: "Domain Territories",
            })}
          </p>
          <div className="grid gap-3 lg:grid-cols-2">
            {domainTerritories.map((territory, index) => (
              <ReadOnlyAudienceItem
                key={`${territory.name}-${index}`}
                title={territory.name}
                description={territory.description}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function BrandProfilePage() {
  const intl = useIntl();
  const { brand, brandQuery } = useRouteBrand();
  const utils = trpc.useUtils();

  const query = trpc.brand.getBrandProfile.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id), staleTime: Infinity },
  );
  const mutation = trpc.brand.updateBrandVoice.useMutation({
    onSuccess: async () => {
      if (!brand?.id) return;

      await utils.brand.getStrategyRegenerationStatus.invalidate({ brandId: brand.id });
    },
  });

  const [form, setForm] = useState<ProfileForm>(EMPTY_FORM);
  const [activeSection, setActiveSection] = useState<SectionKey>("basics");

  useEffect(() => {
    if (query.data) {
      const data = query.data;
      setForm({
        brandName: data.brandName ?? "",
        tagline: data.tagline ?? "",
        mission: data.mission ?? "",
        vision: data.vision ?? "",
        marketCategory: data.marketCategory ?? "",
        marketSubcategoryNiche: data.marketSubcategoryNiche ?? "",
        differentiationStrategy: data.differentiationStrategy ?? "",
        behavioralTraits: data.behavioralTraits ?? "",
        brandPromise: data.brandPromise ?? "",
        brandStoryOrigin: data.brandStoryOrigin ?? "",
        brandArchetype: data.brandArchetype ?? "",
        perceivedPositioning: data.perceivedPositioning ?? [],
        buyingTriggers: data.buyingTriggers ?? [],
        decisionMakingDrivers: data.decisionMakingDrivers ?? [],
        desiresAspirations: data.desiresAspirations ?? [],
        competitiveAdvantages: data.competitiveAdvantages ?? [],
        keyDifferentiators: data.keyDifferentiators?.length
          ? data.keyDifferentiators
          : data.uniqueSellingProposition
            ? [data.uniqueSellingProposition]
            : [],
        painPoints: data.painPoints ?? [],
        competitiveDisadvantages: data.competitiveDisadvantages ?? [],
        coreValues: data.coreValues ?? [],
      });
    }
  }, [query.data]);

  const set = <K extends keyof ProfileForm>(key: K, value: ProfileForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = (key: keyof ProfileForm, value: string | string[]) => {
    if (!brand?.id) return;

    return mutation
      .mutateAsync({ brandId: brand.id, fields: { [FIELD_MAP[key]]: value } })
      .catch(() => {
        toast.error(
          intl.formatMessage({
            id: "common.couldNotSaveChanges",
            defaultMessage: "Could not save changes",
          }),
        );
        throw new Error("save_failed");
      });
  };

  const saveValueProps = (items: string[]) => {
    if (!brand?.id) return;

    return mutation
      .mutateAsync({
        brandId: brand.id,
        fields: {
          "coreIdentity.keyDifferentiators": items,
          "coreIdentity.uniqueSellingProposition": "",
        },
      })
      .catch(() => {
        toast.error(
          intl.formatMessage({
            id: "common.couldNotSaveChanges",
            defaultMessage: "Could not save changes",
          }),
        );
        throw new Error("save_failed");
      });
  };

  if (brandQuery.isPending || query.isPending) {
    return <CenteredLoader />;
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  const data = query.data;
  if (!data) return null;

  const brandName =
    form.brandName ||
    data.brandName ||
    intl.formatMessage({ id: "brandProfile.yourBrand", defaultMessage: "Your brand" });
  const humanLenses = data.humanLenses ?? [];
  const domainTerritories = data.domainTerritories ?? [];
  const hasDeepAudienceInsights = humanLenses.length > 0 || domainTerritories.length > 0;
  const insightCount =
    form.buyingTriggers.length +
    form.decisionMakingDrivers.length +
    form.desiresAspirations.length +
    form.painPoints.length +
    humanLenses.length +
    domainTerritories.length;
  const profileSections = [
    {
      value: "basics" as const,
      label: intl.formatMessage({ id: "brandProfile.sections.basics", defaultMessage: "Basics" }),
    },
    {
      value: "positioning" as const,
      label: intl.formatMessage({
        id: "brandProfile.sections.positioning",
        defaultMessage: "Positioning",
      }),
    },
    {
      value: "insight" as const,
      label: intl.formatMessage({ id: "brandProfile.sections.insight", defaultMessage: "Insight" }),
    },
    {
      value: "proof" as const,
      label: intl.formatMessage({ id: "brandProfile.sections.proof", defaultMessage: "Proof" }),
    },
    {
      value: "story" as const,
      label: intl.formatMessage({ id: "brandProfile.sections.story", defaultMessage: "Story" }),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-6 py-6">
      <PageIntro
        title={intl.formatMessage({ id: "brandProfile.title", defaultMessage: "Brand Profile" })}
        titleClassName="text-balance"
      />

      <div className="flex flex-wrap gap-2">
        {form.tagline ? <Badge variant="secondary">{form.tagline}</Badge> : null}
        {form.marketCategory ? <Badge variant="outline">{form.marketCategory}</Badge> : null}
        {form.brandArchetype ? <Badge variant="outline">{form.brandArchetype}</Badge> : null}
      </div>

      <div className="space-y-4">
        <SegmentedFilter
          options={profileSections}
          value={activeSection}
          onChange={setActiveSection}
        />

        {activeSection === "basics" ? (
          <Section
            title={intl.formatMessage({
              id: "brandProfile.brandBasics.title",
              defaultMessage: "Brand Basics",
            })}
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <EditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.brandName.label",
                  defaultMessage: "Brand Name",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.brandName.description",
                  defaultMessage: "Use the primary public-facing brand name.",
                })}
                value={form.brandName}
                onChange={(value) => set("brandName", value)}
                onSave={(value) => save("brandName", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.brandName.placeholder",
                  defaultMessage: "e.g. Northstar Coffee",
                })}
                rows={3}
              />
              <EditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.tagline.label",
                  defaultMessage: "Tagline",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.tagline.description",
                  defaultMessage: "A short phrase customers should remember.",
                })}
                value={form.tagline}
                onChange={(value) => set("tagline", value)}
                onSave={(value) => save("tagline", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.tagline.placeholder",
                  defaultMessage: "e.g. Coffee that moves with you",
                })}
                rows={3}
              />
              <OptionalEditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.mission.label",
                  defaultMessage: "Mission",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.mission.description",
                  defaultMessage: "What the brand is here to do today.",
                })}
                value={form.mission}
                onChange={(value) => set("mission", value)}
                onSave={(value) => save("mission", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.mission.placeholder",
                  defaultMessage: "What problem do you solve and for whom?",
                })}
              />
              <OptionalEditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.vision.label",
                  defaultMessage: "Vision",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.vision.description",
                  defaultMessage: "What future the brand wants to create.",
                })}
                value={form.vision}
                onChange={(value) => set("vision", value)}
                onSave={(value) => save("vision", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.vision.placeholder",
                  defaultMessage: "What does success look like in a few years?",
                })}
              />
            </div>
          </Section>
        ) : null}

        {activeSection === "positioning" ? (
          <Section
            title={intl.formatMessage({
              id: "brandProfile.positioning.title",
              defaultMessage: "Positioning",
            })}
            badge={intl.formatMessage(
              {
                id: "brandProfile.positioning.badge",
                defaultMessage: "{count} positioning points",
              },
              { count: form.perceivedPositioning.length },
            )}
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <OptionalEditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.marketCategory.label",
                  defaultMessage: "Market Category",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.marketCategory.description",
                  defaultMessage: "The main category customers would place you in.",
                })}
                value={form.marketCategory}
                onChange={(value) => set("marketCategory", value)}
                onSave={(value) => save("marketCategory", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.marketCategory.placeholder",
                  defaultMessage: "e.g. Specialty coffee subscription",
                })}
                rows={3}
              />
              <EditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.marketNiche.label",
                  defaultMessage: "Market Niche",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.marketNiche.description",
                  defaultMessage: "The more precise subcategory or niche you want to own.",
                })}
                value={form.marketSubcategoryNiche}
                onChange={(value) => set("marketSubcategoryNiche", value)}
                onSave={(value) => save("marketSubcategoryNiche", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.marketNiche.placeholder",
                  defaultMessage: "e.g. Single-origin beans for remote teams",
                })}
                rows={3}
              />
            </div>
            <EditableListField
              label={intl.formatMessage({
                id: "brandProfile.perceivedPositioning.label",
                defaultMessage: "Perceived Positioning",
              })}
              description={intl.formatMessage({
                id: "brandProfile.perceivedPositioning.description",
                defaultMessage: "How you want customers to describe the brand in simple phrases.",
              })}
              items={form.perceivedPositioning}
              emptyStateLabel={intl.formatMessage({
                id: "brandProfile.perceivedPositioning.empty",
                defaultMessage: "No positioning statements yet",
              })}
              addLabel={intl.formatMessage({
                id: "brandProfile.perceivedPositioning.add",
                defaultMessage: "Add positioning point",
              })}
              onSave={async (items) => {
                set("perceivedPositioning", items);
                await save("perceivedPositioning", items);
              }}
            />
            <OptionalEditableTextarea
              label={intl.formatMessage({
                id: "brandProfile.differentiationStrategy.label",
                defaultMessage: "Differentiation Strategy",
              })}
              description={intl.formatMessage({
                id: "brandProfile.differentiationStrategy.description",
                defaultMessage: "Explain the wedge that sets this brand apart from alternatives.",
              })}
              value={form.differentiationStrategy}
              onChange={(value) => set("differentiationStrategy", value)}
              onSave={(value) => save("differentiationStrategy", value)}
              placeholder={intl.formatMessage({
                id: "brandProfile.differentiationStrategy.placeholder",
                defaultMessage:
                  "Why should customers choose this brand over the obvious alternatives?",
              })}
            />
            <CompetitorEditor
              direct={data.directCompetitors}
              indirect={data.indirectCompetitors}
              substitutes={data.substituteSolutions}
              onSave={(buckets) => {
                if (!brand.id) return Promise.resolve();
                return mutation.mutateAsync({
                  brandId: brand.id,
                  fields: {
                    "positioningMarketContext.directCompetitors": buckets.direct,
                    "positioningMarketContext.indirectCompetitors": buckets.indirect,
                    "positioningMarketContext.substituteSolutions": buckets.substitutes,
                  },
                });
              }}
            />
          </Section>
        ) : null}

        {activeSection === "insight" ? (
          <Section
            title={intl.formatMessage({
              id: "brandProfile.customerInsight.title",
              defaultMessage: "Customer Insight",
            })}
            badge={intl.formatMessage(
              {
                id: "brandProfile.customerInsight.badge",
                defaultMessage: "{count} insight points",
              },
              { count: insightCount },
            )}
          >
            <OptionalEditableTextarea
              label={intl.formatMessage({
                id: "brandProfile.behavioralTraits.label",
                defaultMessage: "Behavioral Traits",
              })}
              description={intl.formatMessage({
                id: "brandProfile.behavioralTraits.description",
                defaultMessage:
                  "How customers research, compare, buy, and use the product or service.",
              })}
              value={form.behavioralTraits}
              onChange={(value) => set("behavioralTraits", value)}
              onSave={(value) => save("behavioralTraits", value)}
              placeholder={intl.formatMessage({
                id: "brandProfile.behavioralTraits.placeholder",
                defaultMessage: "What patterns show up in their behavior?",
              })}
            />
            <div className="grid gap-5 lg:grid-cols-2">
              <EditableListField
                label={intl.formatMessage({
                  id: "brandProfile.buyingTriggers.label",
                  defaultMessage: "Buying Triggers",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.buyingTriggers.description",
                  defaultMessage: "Events or realizations that make customers ready to act.",
                })}
                items={form.buyingTriggers}
                emptyStateLabel={intl.formatMessage({
                  id: "brandProfile.buyingTriggers.empty",
                  defaultMessage: "No triggers captured yet",
                })}
                addLabel={intl.formatMessage({
                  id: "brandProfile.buyingTriggers.add",
                  defaultMessage: "Add trigger",
                })}
                onSave={async (items) => {
                  set("buyingTriggers", items);
                  await save("buyingTriggers", items);
                }}
              />
              <EditableListField
                label={intl.formatMessage({
                  id: "brandProfile.decisionMakingDrivers.label",
                  defaultMessage: "Decision-Making Drivers",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.decisionMakingDrivers.description",
                  defaultMessage:
                    "Criteria customers use to compare options and justify the choice.",
                })}
                items={form.decisionMakingDrivers}
                emptyStateLabel={intl.formatMessage({
                  id: "brandProfile.decisionMakingDrivers.empty",
                  defaultMessage: "No decision drivers yet",
                })}
                addLabel={intl.formatMessage({
                  id: "brandProfile.decisionMakingDrivers.add",
                  defaultMessage: "Add driver",
                })}
                onSave={async (items) => {
                  set("decisionMakingDrivers", items);
                  await save("decisionMakingDrivers", items);
                }}
              />
              <EditableListField
                label={intl.formatMessage({
                  id: "brandProfile.desiresAspirations.label",
                  defaultMessage: "Desires & Aspirations",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.desiresAspirations.description",
                  defaultMessage: "The outcomes or emotional states customers want to reach.",
                })}
                items={form.desiresAspirations}
                emptyStateLabel={intl.formatMessage({
                  id: "brandProfile.desiresAspirations.empty",
                  defaultMessage: "No aspirations yet",
                })}
                addLabel={intl.formatMessage({
                  id: "brandProfile.desiresAspirations.add",
                  defaultMessage: "Add aspiration",
                })}
                onSave={async (items) => {
                  set("desiresAspirations", items);
                  await save("desiresAspirations", items);
                }}
              />
              <EditableListField
                label={intl.formatMessage({
                  id: "brandProfile.painPoints.label",
                  defaultMessage: "Pain Points",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.painPoints.description",
                  defaultMessage: "Problems, frustrations, or blockers customers want resolved.",
                })}
                items={form.painPoints}
                emptyStateLabel={intl.formatMessage({
                  id: "brandProfile.painPoints.empty",
                  defaultMessage: "No pain points captured yet",
                })}
                addLabel={intl.formatMessage({
                  id: "brandProfile.painPoints.add",
                  defaultMessage: "Add pain point",
                })}
                onSave={async (items) => {
                  set("painPoints", items);
                  await save("painPoints", items);
                }}
              />
            </div>
            {hasDeepAudienceInsights ? (
              <DeepAudienceInsights
                humanLenses={humanLenses}
                domainTerritories={domainTerritories}
              />
            ) : null}
          </Section>
        ) : null}

        {activeSection === "proof" ? (
          <Section
            title={intl.formatMessage(
              {
                id: "brandProfile.proof.title",
                defaultMessage: "Why Customers Choose {brandName}",
              },
              { brandName },
            )}
            badge={intl.formatMessage(
              { id: "brandProfile.proof.badge", defaultMessage: "{count} proof points" },
              { count: form.competitiveAdvantages.length + form.keyDifferentiators.length },
            )}
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <EditableListField
                label={intl.formatMessage({
                  id: "brandProfile.competitiveAdvantages.label",
                  defaultMessage: "Competitive Advantages",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.competitiveAdvantages.description",
                  defaultMessage: "Operational or product strengths the brand genuinely has.",
                })}
                items={form.competitiveAdvantages}
                emptyStateLabel={intl.formatMessage({
                  id: "brandProfile.competitiveAdvantages.empty",
                  defaultMessage: "No advantages documented yet",
                })}
                addLabel={intl.formatMessage({
                  id: "brandProfile.competitiveAdvantages.add",
                  defaultMessage: "Add advantage",
                })}
                onSave={async (items) => {
                  set("competitiveAdvantages", items);
                  await save("competitiveAdvantages", items);
                }}
              />
              <EditableListField
                label={intl.formatMessage({
                  id: "brandProfile.keyDifferentiators.label",
                  defaultMessage: "Key differentiators",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.keyDifferentiators.description",
                  defaultMessage: "Short, memorable reasons customers should choose this brand.",
                })}
                items={form.keyDifferentiators}
                emptyStateLabel={intl.formatMessage({
                  id: "brandProfile.keyDifferentiators.empty",
                  defaultMessage: "No differentiators yet",
                })}
                addLabel={intl.formatMessage({
                  id: "brandProfile.keyDifferentiators.add",
                  defaultMessage: "Add differentiator",
                })}
                onSave={async (items) => {
                  set("keyDifferentiators", items);
                  await saveValueProps(items);
                }}
              />
              <OptionalEditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.brandPromise.label",
                  defaultMessage: "Brand Promise",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.brandPromise.description",
                  defaultMessage:
                    "What customers should consistently expect every time they engage with the brand.",
                })}
                value={form.brandPromise}
                onChange={(value) => set("brandPromise", value)}
                onSave={(value) => save("brandPromise", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.brandPromise.placeholder",
                  defaultMessage: "What experience or outcome do you promise to deliver?",
                })}
              />
            </div>
            <EditableListField
              label={intl.formatMessage({
                id: "brandProfile.competitiveDisadvantages.label",
                defaultMessage: "Competitive Disadvantages",
              })}
              description={intl.formatMessage({
                id: "brandProfile.competitiveDisadvantages.description",
                defaultMessage:
                  "Weak spots, objections, or perceptions the brand should proactively manage.",
              })}
              items={form.competitiveDisadvantages}
              emptyStateLabel={intl.formatMessage({
                id: "brandProfile.competitiveDisadvantages.empty",
                defaultMessage: "No risks documented yet",
              })}
              addLabel={intl.formatMessage({
                id: "brandProfile.competitiveDisadvantages.add",
                defaultMessage: "Add risk",
              })}
              onSave={async (items) => {
                set("competitiveDisadvantages", items);
                await save("competitiveDisadvantages", items);
              }}
            />
          </Section>
        ) : null}

        {activeSection === "story" ? (
          <Section
            title={intl.formatMessage(
              { id: "brandProfile.story.title", defaultMessage: "The {brandName} Story" },
              { brandName },
            )}
            badge={intl.formatMessage(
              { id: "brandProfile.story.badge", defaultMessage: "{count} core values" },
              { count: form.coreValues.length },
            )}
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <OptionalEditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.origin.label",
                  defaultMessage: "Origin",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.origin.description",
                  defaultMessage:
                    "How the brand started, including the trigger or problem that made it exist.",
                })}
                value={form.brandStoryOrigin}
                onChange={(value) => set("brandStoryOrigin", value)}
                onSave={(value) => save("brandStoryOrigin", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.origin.placeholder",
                  defaultMessage: "What is the founding story in plain language?",
                })}
              />
              <EditableTextarea
                label={intl.formatMessage({
                  id: "brandProfile.brandArchetype.label",
                  defaultMessage: "Brand Archetype",
                })}
                description={intl.formatMessage({
                  id: "brandProfile.brandArchetype.description",
                  defaultMessage: "The dominant character or role the brand tends to play.",
                })}
                value={form.brandArchetype}
                onChange={(value) => set("brandArchetype", value)}
                onSave={(value) => save("brandArchetype", value)}
                placeholder={intl.formatMessage({
                  id: "brandProfile.brandArchetype.placeholder",
                  defaultMessage: "e.g. Guide, Rebel, Caregiver",
                })}
              />
            </div>
            <EditableListField
              label={intl.formatMessage({
                id: "brandProfile.coreValues.label",
                defaultMessage: "Core Values",
              })}
              description={intl.formatMessage({
                id: "brandProfile.coreValues.description",
                defaultMessage:
                  "Principles that should consistently shape decisions, messaging, and behavior.",
              })}
              items={form.coreValues}
              emptyStateLabel={intl.formatMessage({
                id: "brandProfile.coreValues.empty",
                defaultMessage: "No core values defined yet",
              })}
              addLabel={intl.formatMessage({
                id: "brandProfile.coreValues.add",
                defaultMessage: "Add value",
              })}
              onSave={async (items) => {
                set("coreValues", items);
                await save("coreValues", items);
              }}
            />
          </Section>
        ) : null}
      </div>
    </div>
  );
}
